import { BigBashEvent, Game, GamePoint } from "@/types/types";
import SmallHelpers from "@/utils/SmallHelpers";

class GameResultsCalculator {
	private readonly PLACEMENT_POINTS = [15, 12, 10, 9, 8, 7, 6, 5, 4, 3, 1, 0];
	private smallHelpers = new SmallHelpers();

	/**
	 * Calculate the placement points for a single game using placement points.
	 * Returns placement points for each team.
	 */
	public getRankedGameResults(game: Game, event: BigBashEvent): Map<string, number> {
		if (this._hasValidGameResultsData(event, game)) {
			const gamePoints = this.smallHelpers.getGamePointsForGame(event, game.id);
			return this._calculateTeamTotalsAcrossAllGameTypes(gamePoints);
		}
		return new Map<string, number>();
	}


	private _calculateTeamTotalsAcrossAllGameTypes(gamePoints: GamePoint[]): Map<string, number> {
		const gameResults = new Map<string, number>();

		const mostPointsRanked = this._calculateMostPointsRankedPoints(gamePoints);
		const timeRankedPoints = this._calculateFastestTimeRankedPoints(gamePoints);
		const differenceRankedPoints = this._calculateSmallestDifferenceRankedPoints(gamePoints);

		const allTeamIds = new Set<string>();
		mostPointsRanked.forEach((_, teamId) => allTeamIds.add(teamId));
		timeRankedPoints.forEach((_, teamId) => allTeamIds.add(teamId));
		differenceRankedPoints.forEach((_, teamId) => allTeamIds.add(teamId));

		allTeamIds.forEach(teamId => {
			const total =
				(mostPointsRanked.get(teamId) ?? 0) +
				(timeRankedPoints.get(teamId) ?? 0) +
				(differenceRankedPoints.get(teamId) ?? 0);
			gameResults.set(teamId, total);
		});

		return gameResults;
	}

	/**
	 * Get game results for display purposes.
	 * Returns raw points for most_points games, ranked points for others.
	 */
	public getGameResults(game: Game, event: BigBashEvent): Map<string, number> {
		if (this._hasValidGameResultsData(event, game)) {
			const gamePoints = this.smallHelpers.getGamePointsForGame(event, game.id);

			if (game.point_calculation === 'most_points') {
				return this.calculateMostPointsRawTotals(gamePoints);
			}

			return this._calculateTeamTotalsAcrossAllGameTypes(gamePoints);
		}
		return new Map<string, number>();
	}

	// MOST POINTS

	/**
	 * Calculate raw point totals for most points games.
	 * Returns the actual sum of points per team without placement ranking.
	 */
	public calculateMostPointsRawTotals(gamePoints: GamePoint[]): Map<string, number> {
		const teamTotalRawPoints = new Map<string, number>();

		gamePoints.forEach(gamePoint => {
			let teamTotal = 0;
			gamePoint.rounds.forEach(round => {
				if (round.points != null) {
					teamTotal += round.points;
				}
			});
			teamTotalRawPoints.set(gamePoint.team, teamTotal);
		});

		return teamTotalRawPoints;
	}

	/**
	 * Calculate placement points for most points games.
	 * Sum each team's raw points across all rounds, then rank by total.
	 */
	private _calculateMostPointsRankedPoints(gamePoints: GamePoint[]): Map<string, number> {
		const teamsWithGamePoints = gamePoints.filter(gp =>
			gp.rounds.some(r => r.points != null)
		);

		if (teamsWithGamePoints.length === 0) {
			return new Map<string, number>();
		}

		const teamTotals = this.calculateMostPointsRawTotals(teamsWithGamePoints);
		return this._rankTeamsAndAssignPlacementPoints(teamTotals, 'desc');
	}

	// FASTEST TIME

	/**
	 * Calculate placement points for fastest time games.
	 * 1. Exclude teams with no round data
	 * 2. Per-round ranking - fastest time gets placement points, missing = last place
	 * 3. Sum placement points per team
	 * 4. Rank by total placement points and assign final placement points
	 */
	private _calculateFastestTimeRankedPoints(gamePoints: GamePoint[]): Map<string, number> {
		// Filter out teams with no valid rounds
		const teamsWithData = gamePoints.filter(gp =>
			gp.rounds.some(r => r.team_fastest_time_seconds != null && r.team_fastest_time_seconds > 0)
		);

		if (teamsWithData.length === 0) {
			return new Map<string, number>();
		}

		// Get all round numbers that have time data
		const roundNumbers = this._extractUniqueRoundNumbers(gamePoints, 'team_fastest_time_seconds');

		if (roundNumbers.size === 0) {
			return new Map<string, number>();
		}

		// Step 1 & 2: Calculate placement points per round
		const teamPlacementPointsPerRound = new Map<string, number[]>();

		// Initialize arrays for all teams with data
		teamsWithData.forEach(gp => {
			teamPlacementPointsPerRound.set(gp.team, []);
		});

		roundNumbers.forEach(roundNumber => {
			// Get times for teams that have data for this round
			const teamTimes = new Map<string, number>();
			const teamsWithRoundData = new Set<string>();

			teamsWithData.forEach(gp => {
				const round = gp.rounds.find(r => r.round === roundNumber);
				if (round && round.team_fastest_time_seconds != null && round.team_fastest_time_seconds > 0) {
					teamTimes.set(gp.team, round.team_fastest_time_seconds);
					teamsWithRoundData.add(gp.team);
				}
			});

			// Rank teams that have data for this round (ascending - lower time is better)
			const roundPlacementPoints = this._rankTeamsAndAssignPlacementPoints(teamTimes, 'asc');

			// Get last place points for teams missing this round
			const lastPlacePoints = this.smallHelpers.getPointsForRank(
				teamsWithData.length - 1,
				this.PLACEMENT_POINTS
			);

			// Assign placement points: actual rank or last place if missing
			teamsWithData.forEach(gp => {
				const points = teamsWithRoundData.has(gp.team)
					? roundPlacementPoints.get(gp.team) ?? lastPlacePoints
					: lastPlacePoints;
				teamPlacementPointsPerRound.get(gp.team)!.push(points);
			});
		});

		// Step 3: Sum placement points per team
		const teamTotalPlacementPoints = new Map<string, number>();

		teamPlacementPointsPerRound.forEach((pointsArray, teamId) => {
			const total = pointsArray.reduce((sum, p) => sum + p, 0);
			teamTotalPlacementPoints.set(teamId, total);
		});

		// Step 4: Final ranking (higher total = better, so 'desc')
		return this._rankTeamsAndAssignPlacementPoints(teamTotalPlacementPoints, 'desc');
	}

	// SMALLEST DIFFERENCE 

	/**
	 * Calculate placement points for smallest difference games.
	 * 1. Exclude teams with no round data
	 * 2. Per-round ranking - smallest difference gets placement points, missing = last place
	 * 3. Sum placement points per team
	 * 4. Rank by total placement points and assign final placement points
	 */
	private _calculateSmallestDifferenceRankedPoints(gamePoints: GamePoint[]): Map<string, number> {
		// Filter out teams with no valid rounds
		const teamsWithData = gamePoints.filter(gp =>
			gp.rounds.some(r => r.team_smallest_difference_value != null)
		);

		if (teamsWithData.length === 0) {
			return new Map<string, number>();
		}

		// Get all round numbers that have target values
		const roundNumbers = this._extractUniqueRoundNumbersForSmallestDifference(gamePoints);

		if (roundNumbers.size === 0) {
			return new Map<string, number>();
		}

		// Step 1 & 2: Calculate placement points per round
		const teamPlacementPointsPerRound = new Map<string, number[]>();

		// Initialize arrays for all teams with data
		teamsWithData.forEach(gp => {
			teamPlacementPointsPerRound.set(gp.team, []);
		});

		roundNumbers.forEach(roundNumber => {
			const targetValue = this._getTargetValueForRound(gamePoints, roundNumber);

			if (targetValue === null) {
				return;
			}

			// Get differences for teams that have data for this round
			const teamDifferences = new Map<string, number>();
			const teamsWithRoundData = new Set<string>();

			teamsWithData.forEach(gp => {
				const round = gp.rounds.find(r => r.round === roundNumber);
				if (round && round.team_smallest_difference_value != null) {
					const difference = Math.abs(round.team_smallest_difference_value - targetValue);
					teamDifferences.set(gp.team, difference);
					teamsWithRoundData.add(gp.team);
				}
			});

			// Rank teams that have data for this round
			const roundPlacementPoints = this._rankTeamsAndAssignPlacementPoints(teamDifferences, 'asc');

			// Get last place points for teams missing this round
			const lastPlacePoints = this.smallHelpers.getPointsForRank(
				teamsWithData.length - 1,
				this.PLACEMENT_POINTS
			);

			// Assign placement points: actual rank or last place if missing
			teamsWithData.forEach(gp => {
				const points = teamsWithRoundData.has(gp.team)
					? roundPlacementPoints.get(gp.team) ?? lastPlacePoints
					: lastPlacePoints;
				teamPlacementPointsPerRound.get(gp.team)!.push(points);
			});
		});

		// Step 3: Sum placement points per team
		const teamTotalPlacementPoints = new Map<string, number>();

		teamPlacementPointsPerRound.forEach((pointsArray, teamId) => {
			const total = pointsArray.reduce((sum, p) => sum + p, 0);
			teamTotalPlacementPoints.set(teamId, total);
		});

		// Step 4: Final ranking (higher total = better, so 'desc')
		return this._rankTeamsAndAssignPlacementPoints(teamTotalPlacementPoints, 'desc');
	}

	private _rankTeamsAndAssignPlacementPoints(
		teamValues: Map<string, number>,
		sortOrder: 'asc' | 'desc'
	): Map<string, number> {
		const sorted = Array.from(teamValues.entries()).sort((a, b) => {
			return sortOrder === 'asc' ? a[1] - b[1] : b[1] - a[1];
		});

		const placementPoints = new Map<string, number>();
		let placementIndex = 0;

		for (let i = 0; i < sorted.length; i++) {
			const [teamId, value] = sorted[i];

			if (i > 0 && value !== sorted[i - 1][1]) {
				placementIndex++;
			}

			const points = this.smallHelpers.getPointsForRank(placementIndex, this.PLACEMENT_POINTS);
			placementPoints.set(teamId, points);
		}

		return placementPoints;
	}

	private _extractUniqueRoundNumbersForSmallestDifference(gamePoints: GamePoint[]): Set<number> {
		const roundNumbers = new Set<number>();

		gamePoints.forEach(gamePoint => {
			gamePoint.rounds.forEach(round => {
				if (round.team_smallest_difference_value != null) {
					roundNumbers.add(round.round);
				}
			});
		});

		return roundNumbers;
	}

	private _extractUniqueRoundNumbers(
		gamePoints: GamePoint[],
		valueKey: 'points' | 'team_fastest_time_seconds'
	): Set<number> {
		const roundNumbers = new Set<number>();

		gamePoints.forEach(gamePoint => {
			gamePoint.rounds.forEach(round => {
				const value = round[valueKey];
				if (value != null && value > 0) {
					roundNumbers.add(round.round);
				}
			});
		});

		return roundNumbers;
	}

	private _getTargetValueForRound(gamePoints: GamePoint[], roundNumber: number): number | null {
		for (const gamePoint of gamePoints) {
			const round = gamePoint.rounds.find(r => r.round === roundNumber);
			if (round && round.round_target_value != null) {
				return round.round_target_value;
			}
		}
		return null;
	}

	private _hasValidGameResultsData(event: BigBashEvent, game: Game): boolean {
		if (!event) {
			throw new Error('Event is undefined.');
		} else if (!game) {
			throw new Error('Game is undefined.');
		} else if (!event.game_points || event.game_points.length === 0) {
			throw new Error('No game points available.');
		}
		return true;
	}
}

export default GameResultsCalculator;